import type {
  AssessmentStatus,
  Criticality,
  EvidenceValidity,
  VendorFindingSeverity,
  VendorFindingState,
  LifecycleState,
  ProposalDecision,
  ProposalOutcome,
  RiskTier,
} from "@odingard/sdk-client";

export const RISK_TIERS: RiskTier[] = ["TIER_1", "TIER_2", "TIER_3", "TIER_4"];
export const CRITICALITIES: Criticality[] = ["CRITICAL", "HIGH", "MODERATE", "LOW"];
export const LIFECYCLE_STATES: LifecycleState[] = [
  "DRAFT",
  "IN_REVIEW",
  "APPROVED",
  "REJECTED",
  "ACTIVE",
  "SUSPENDED",
  "OFFBOARDING",
  "OFFBOARDED",
];

export const TIER_LABEL: Record<string, string> = {
  TIER_1: "Tier 1",
  TIER_2: "Tier 2",
  TIER_3: "Tier 3",
  TIER_4: "Tier 4",
};

export const STATE_LABEL: Record<string, string> = {
  DRAFT: "Draft",
  IN_REVIEW: "In review",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  ACTIVE: "Active",
  SUSPENDED: "Suspended",
  OFFBOARDING: "Offboarding",
  OFFBOARDED: "Offboarded",
};

export const CRITICALITY_LABEL: Record<string, string> = {
  CRITICAL: "Critical",
  HIGH: "High",
  MODERATE: "Moderate",
  LOW: "Low",
};

type Tone = "neutral" | "success" | "warning" | "danger" | "info";

export function tierTone(tier: RiskTier): Tone {
  if (tier === "TIER_1") return "danger";
  if (tier === "TIER_2") return "warning";
  return "neutral";
}

export function stateTone(state: LifecycleState): Tone {
  if (state === "ACTIVE" || state === "APPROVED") return "success";
  if (state === "SUSPENDED" || state === "REJECTED") return "danger";
  if (state === "IN_REVIEW" || state === "OFFBOARDING") return "warning";
  return "neutral";
}

export function validityTone(validity: EvidenceValidity): Tone {
  if (validity === "VALID") return "success";
  if (validity === "EXPIRING" || validity === "NOT_YET_EFFECTIVE") return "warning";
  if (validity === "EXPIRED" || validity === "WITHDRAWN") return "danger";
  return "neutral";
}

export const VALIDITY_LABEL: Record<string, string> = {
  VALID: "Valid",
  EXPIRING: "Expiring",
  EXPIRED: "Expired",
  NOT_YET_EFFECTIVE: "Not yet effective",
  SUPERSEDED: "Superseded",
  WITHDRAWN: "Withdrawn",
};

// ----- R2 -------------------------------------------------------------------

export const FINDING_STATES: VendorFindingState[] = ["OPEN", "IN_PROGRESS", "ACCEPTED_RISK", "RESOLVED"];
export const SEVERITIES: VendorFindingSeverity[] = ["CRITICAL", "HIGH", "MODERATE", "LOW"];

export const ASSESSMENT_STATUS_LABEL: Record<AssessmentStatus, string> = {
  OPEN: "Open",
  SUBMITTED: "Submitted",
  CANCELLED: "Cancelled",
};

export const FINDING_STATE_LABEL: Record<VendorFindingState, string> = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  ACCEPTED_RISK: "Risk accepted",
  RESOLVED: "Resolved",
};

export const SEVERITY_LABEL: Record<VendorFindingSeverity, string> = {
  CRITICAL: "Critical",
  HIGH: "High",
  MODERATE: "Moderate",
  LOW: "Low",
};

/**
 * Said in words rather than left as an enum, because the whole point of an
 * outcome is that a reader can tell a drafted answer from a declined one.
 */
export const OUTCOME_LABEL: Record<ProposalOutcome, string> = {
  PROPOSED: "Draft answer",
  UNSUPPORTED: "Declined: nothing on file supports an answer",
  CONTRADICTED: "Declined: the evidence on file disagrees with itself",
};

export const DECISION_LABEL: Record<ProposalDecision, string> = {
  PENDING: "Awaiting a person",
  ACCEPTED: "Accepted as written",
  EDITED: "Accepted with edits",
  REJECTED: "Rejected",
};

export function assessmentStatusTone(status: AssessmentStatus): Tone {
  if (status === "SUBMITTED") return "success";
  if (status === "CANCELLED") return "neutral";
  return "info";
}

export function findingStateTone(state: VendorFindingState): Tone {
  if (state === "RESOLVED") return "success";
  if (state === "ACCEPTED_RISK") return "warning";
  if (state === "IN_PROGRESS") return "info";
  return "danger";
}

export function severityTone(severity: VendorFindingSeverity): Tone {
  if (severity === "CRITICAL") return "danger";
  if (severity === "HIGH") return "warning";
  return "neutral";
}

/** A refusal carries no value, so it can be rejected or answered by hand only. */
export function isRefusal(outcome: ProposalOutcome): boolean {
  return outcome !== "PROPOSED";
}
